import React, { useState, useEffect } from 'react';
import { CircularProgress } from '@material-ui/core';
import axios from 'axios';
import qs from 'query-string';
import PropTypes from 'prop-types';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import { withStyles } from '@material-ui/core/styles';
import Select from '@material-ui/core/Select';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import InputLabel from '@material-ui/core/InputLabel';
import FormControl from '@material-ui/core/FormControl';
import ContentHeader from '../components/ContentHeader';

const styles = theme => ({
    root: {
        background: '#fff',
        boxShadow: '0px 2px 5px 0px rgba(0,0,0,0.21)',
        border: '0',
        maxWidth: '600px',
        margin: '40px auto',
        position: 'relative',
        textAlign: 'center',
        minHeight: '300px',
        [theme.breakpoints.down('sm')]: {
            maxWidth: '100%',
            margin: '20px 15px'
        }
    },
    intro: {
        marginTop: 30
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '360px',
        margin: '20px auto 40px',
        padding: '20px 10px 20px'
    },
    input: {
        margin: '0 20px 20px'
    },
    yearSelect: {
        textAlign: 'left'
    },
    serviceSelect: {
        textAlign: 'left'
    },
    passengerCount: {
        textAlign: 'right'
    },
    passengers: {
        display: 'flex',
        flexDirection: 'column',
        margin: '10px 0',
        padding: '25px 0 5px',
        border: '1px dashed #ccc'
    },
    submit: {
        width: '200px',
        height: '56px',
        margin: '20px auto'
    },
    message: {
        display: 'inline-block',
        margin: '130px auto'
    },
    spinner: {
        margin: '130px auto'
    },
    formControl: {
        margin: '0 20px 20px',
        display: 'flex'
    },
    'error > div': {
        border: '1px solid red'
    }
});

const WaitingList = ({ classes, match, location }) => {
    const {
        params: { clientSlug, year }
    } = match;

    const [isLoading, setIsLoading] = useState(true);
    const [submitted, setSubmitted] = useState(false);
    const [submitFailed, setSubmitFailed] = useState(false);
    const [data, setData] = useState(null);

    const cleanYear = year ? year.replace('-', '/') : '';

    const [email, setEmail] = useState('');
    const [mobile, setMobile] = useState('');
    const [service, setService] = useState('');
    const [academicYear, setYear] = useState(cleanYear);
    const [postCode, setPostCode] = useState('');
    const [passengerCount, setPassengerCount] = useState(1);
    const [passengers, setPassengers] = useState([
        {
            firstName: '',
            lastName: ''
        }
    ]);

    const [isServiceValid, setIsServiceValid] = useState(true);
    const [isYearValid, setIsYearValid] = useState(true);

    const sortServices = services => {
        return services.sort((a, b) => {
            const serviceA = a;
            const serviceB = b;

            const aMatches = serviceA.match(/([A-Za-z]+)([0-9]+)/);
            const bMatches = serviceB.match(/([A-Za-z]+)([0-9]+)/);

            if (!aMatches || !bMatches) {
                return serviceA.localeCompare(serviceB);
            }

            if (aMatches.length < 3 || bMatches.length < 3) {
                return serviceA.localeCompare(serviceB);
            }

            if (aMatches[1] === bMatches[1]) {
                return parseInt(aMatches[2], 10) - parseInt(bMatches[2], 10);
            }

            return aMatches[1].localeCompare(bMatches[1]);
        });
    };

    useEffect(() => {
        async function fetchWaitingList() {
            try {
                const response = await axios.get(
                    `${
                        process.env.REACT_APP_WAITING_LIST_API_ENDPOINT
                    }/waiting-list/client/${clientSlug}`
                );

                if (response.data && response.data.services.length) {
                    response.data.services = sortServices(
                        response.data.services
                    );
                }

                setData(response.data);

                if (response.data.services && response.data.services.length) {
                    const query = qs.parse(location.search);
                    const serviceParam = query.service || '';
                    const serviceMatch = response.data.services.find(
                        item => item === serviceParam
                    );

                    if (serviceMatch) {
                        setService(serviceMatch);
                    }
                }

                setIsLoading(false);
            } catch (error) {
                setIsLoading(false);
            }
        }

        fetchWaitingList();
    }, []);

    const onSubmit = async e => {
        e.preventDefault();

        if (data.services && data.services.length && service === '') {
            setIsServiceValid(false);
            return;
        }

        if (
            !(data.year && data.year.length && data.year.includes(academicYear))
        ) {
            setIsYearValid(false);
            return;
        }

        setIsLoading(true);

        try {
            const query = qs.parse(location.search);
            const ticketName = query.ticket || '';

            await axios.post(
                `${
                    process.env.REACT_APP_WAITING_LIST_API_ENDPOINT
                }/waiting-list/passenger`,
                {
                    email,
                    mobile,
                    postCode,
                    passengers,
                    year: academicYear,
                    service,
                    clientSlug,
                    ticketName
                }
            );
            setIsLoading(false);
            setSubmitted(true);
        } catch (error) {
            setIsLoading(false);
            setSubmitFailed(true);
        }
    };

    const handleChange = setFunc => ({ target }) => setFunc(target.value);

    const handleServiceChange = ({ target }) => {
        setService(target.value);
        setIsServiceValid(true);
    };

    const handleYearChange = ({ target }) => {
        setYear(target.value);
        setIsYearValid(true);
    };

    const handlePassengerCount = ({ target }) => {
        const count = parseInt(target.value, 10);
        setPassengerCount(count);
        setPassengers(prevState => {
            const newSlice = prevState.slice(0, count);

            if (newSlice.length !== count) {
                const diff = count - newSlice.length;

                for (let i = 0; i < diff; i += 1) {
                    newSlice.push({
                        firstName: '',
                        lastName: ''
                    });
                }
            }

            return newSlice;
        });
    };

    const renderTextField = (id, label, value, onChange) => (
        <TextField
            autoComplete={id}
            className={classes.input}
            id={id}
            type={id}
            label={label}
            InputLabelProps={{
                shrink: true
            }}
            value={value}
            onChange={onChange}
            variant="outlined"
            required
        />
    );

    const renderPassengerInputs = (passenger, index) => (
        <div key={`passenger-${index}`} className={classes.passengers}>
            {renderTextField(
                `passenger-first-${index}`,
                `Passenger ${index + 1} first name`,
                passenger.firstName,
                ({ target }) => {
                    setPassengers(prevState =>
                        prevState.map((item, i) => {
                            if (index !== i) {
                                return item;
                            }

                            return {
                                firstName: target.value,
                                lastName: passenger.lastName
                            };
                        })
                    );
                }
            )}
            {renderTextField(
                `passenger-last-${index}`,
                `Passenger ${index + 1} last name`,
                passenger.lastName,
                ({ target }) => {
                    setPassengers(prevState =>
                        prevState.map((item, i) => {
                            if (index !== i) {
                                return item;
                            }

                            return {
                                firstName: passenger.firstName,
                                lastName: target.value
                            };
                        })
                    );
                }
            )}
        </div>
    );

    if (isLoading) {
        return (
            <div className={classes.root}>
                <CircularProgress
                    id="loading-spinner"
                    className={classes.spinner}
                />
            </div>
        );
    }

    if (!isLoading && !data) {
        return (
            <div className={classes.root}>
                <span className={classes.message}>
                    Cannot find waiting list
                </span>
            </div>
        );
    }

    if (submitted) {
        return (
            <div className={classes.root}>
                <div className={classes.message}>
                    <p>Thanks for joining the waiting list.</p>
                    <p>
                        We{"'"}ve sent a confirmation email to {email}.
                    </p>
                    <p>
                        {data.name} will be in contact when there is
                        availability on their service.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className={classes.root}>
            <ContentHeader text="Waiting List" id="waiting-list" />
            <p className={classes.intro}>
                Join the waiting list for {data.name}.
            </p>
            <form className={classes.form} onSubmit={onSubmit}>
                {renderTextField(
                    'email',
                    'Your email',
                    email,
                    handleChange(setEmail)
                )}
                {renderTextField(
                    'mobile',
                    'Your mobile number',
                    mobile,
                    handleChange(setMobile)
                )}
                {renderTextField(
                    'post-code',
                    'Your post code',
                    postCode,
                    handleChange(setPostCode)
                )}
                <FormControl variant="outlined" className={classes.formControl}>
                    <InputLabel style={{ background: 'white' }}>
                        Year
                    </InputLabel>
                    <Select
                        id="year"
                        name="year"
                        value={academicYear}
                        onChange={handleYearChange}
                        variant="outlined"
                        className={`${classes.yearSelect} ${
                            !isYearValid ? 'error' : ''
                        }`}
                        required
                        input={
                            <OutlinedInput
                                name="year"
                                labelWidth={0}
                                id="outlined-year"
                            />
                        }
                    >
                        {data.year.map((item, i) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <MenuItem key={`year-${i}`} value={item}>
                                {item}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                {data.services && data.services.length > 0 && (
                    <FormControl
                        variant="outlined"
                        className={classes.formControl}
                    >
                        <InputLabel style={{ background: 'white' }}>
                            Service
                        </InputLabel>
                        <Select
                            id="service"
                            name="service"
                            value={service}
                            onChange={handleServiceChange}
                            variant="outlined"
                            className={`${classes.serviceSelect} ${
                                !isServiceValid ? 'error' : ''
                            }`}
                            required
                            input={
                                <OutlinedInput
                                    name="service"
                                    labelWidth={0}
                                    id="outlined-service"
                                />
                            }
                        >
                            {data.services.map((item, i) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <MenuItem key={`service-${i}`} value={item}>
                                    {item}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
                <TextField
                    className={`${classes.input} ${classes.passengerCount}`}
                    select
                    label="Number of passengers"
                    id="passenger-count"
                    value={passengerCount}
                    onChange={handlePassengerCount}
                    variant="outlined"
                    InputLabelProps={{
                        shrink: true
                    }}
                    required
                >
                    <MenuItem value={1}>1</MenuItem>
                    <MenuItem value={2}>2</MenuItem>
                    <MenuItem value={3}>3</MenuItem>
                    <MenuItem value={4}>4</MenuItem>
                    <MenuItem value={5}>5</MenuItem>
                </TextField>
                {passengers.map(renderPassengerInputs)}
                <p>
                    By submitting this data, I confirm I have read and accepted
                    the{' '}
                    <a
                        href="https://shuttleid.uk/static/shuttleid-terms-of-use-passengers,-purchasers-and-other-users-updated-august-2020-8c80ca7b2c819e755e149871e5a98e88.pdf"
                        rel="noopener noreferrer"
                        target="_blank"
                        style={{ color: '#000' }}
                    >
                        ShuttleID T&amp;Cs
                    </a>
                </p>
                {!isServiceValid && (
                    <p style={{ color: 'red' }}>
                        Please select the service you wish to use.
                    </p>
                )}
                {!isYearValid && (
                    <p style={{ color: 'red' }}>
                        Please select the year you wish to apply for.
                    </p>
                )}
                {submitFailed && (
                    <p style={{ color: 'red' }}>
                        There was a problem handling your submission. Please try
                        again or contact support@shuttleid.uk.
                    </p>
                )}
                <Button
                    variant="outlined"
                    color="primary"
                    className={classes.submit}
                    type="submit"
                >
                    Submit
                </Button>
            </form>
        </div>
    );
};

WaitingList.propTypes = {
    classes: PropTypes.shape().isRequired
};

export default withStyles(styles)(WaitingList);
